import { useState } from "react";
import { Link } from "wouter";
import { PublicLayout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardFooter } from "@/components/ui/card";
import { Loader2, Mail, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { apiFetch } from "@/lib/api";
import { ApiError } from "@workspace/api-client-react";

const schema = z.object({
  email: z.string().email("Invalid email address"),
});

type PageState = "form" | "submitted" | "rate-limited" | "email-failed";

export default function ForgotPassword() {
  const [pageState, setPageState] = useState<PageState>("form");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  });

  async function onSubmit(values: z.infer<typeof schema>) {
    try {
      setIsSubmitting(true);
      await apiFetch("/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email: values.email }),
      });
      setPageState("submitted");
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setPageState("rate-limited");
      } else if (err instanceof ApiError && err.status === 500) {
        setPageState("email-failed");
      } else {
        setPageState("submitted");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <PublicLayout>
      <SEOHead
        title="Forgot Password — OPEN MAT"
        description="Reset your OPEN MAT account password."
        canonicalPath="/forgot-password"
        noindex
      />
      <div className="flex-1 flex items-center justify-center p-4 relative">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary/10 blur-[120px] rounded-full pointer-events-none" />

        <Card className="w-full max-w-md glass-panel border-white/10 z-10 relative overflow-hidden">
          <div className={`absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent ${pageState === "rate-limited" || pageState === "email-failed" ? "via-red-500" : "via-primary"} to-transparent`} />

          <CardHeader className="space-y-1 pb-8 text-center">
            <div className={`mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${pageState === "rate-limited" ? "bg-orange-500/10" : pageState === "email-failed" ? "bg-red-500/10" : "bg-primary/10"}`}>
              {pageState === "submitted" ? (
                <CheckCircle2 className="w-7 h-7 text-primary" />
              ) : pageState === "rate-limited" ? (
                <Clock className="w-7 h-7 text-orange-400" />
              ) : pageState === "email-failed" ? (
                <AlertCircle className="w-7 h-7 text-red-400" />
              ) : (
                <Mail className="w-7 h-7 text-primary" />
              )}
            </div>
            <h1 className="text-3xl font-display font-semibold uppercase tracking-wider text-white leading-none">
              {pageState === "submitted"
                ? "Check Your Email"
                : pageState === "rate-limited"
                ? "Too Many Requests"
                : pageState === "email-failed"
                ? "Delivery Failed"
                : "Forgot Password"}
            </h1>
            <CardDescription className="text-muted-foreground">
              {pageState === "submitted"
                ? "If that email is registered, a reset link has been sent."
                : pageState === "rate-limited"
                ? "You've requested too many reset links in a short time."
                : pageState === "email-failed"
                ? "We couldn't send the reset email. Please try again."
                : "Enter your email and we'll send you a reset link."}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {pageState === "submitted" ? (
              <div className="space-y-4 text-center">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  The link expires in <span className="text-white font-medium">30 minutes</span>. Check your spam folder if you don't see it.
                </p>
                <Button
                  variant="outline"
                  className="w-full h-12 border-white/10 hover:bg-white/5 rounded-xl"
                  onClick={() => {
                    setPageState("form");
                    form.reset();
                  }}
                >
                  Send another link
                </Button>
              </div>
            ) : pageState === "email-failed" ? (
              <div className="space-y-5 text-center">
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-sm text-red-200 leading-relaxed">
                  We couldn't send the reset email right now — please try again shortly. If the problem persists, contact support.
                </div>
                <Button
                  className="w-full h-12 text-lg font-bold bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 rounded-xl"
                  onClick={() => {
                    setPageState("form");
                  }}
                >
                  Try Again
                </Button>
              </div>
            ) : pageState === "rate-limited" ? (
              <div className="space-y-5 text-center">
                <div className="p-4 rounded-xl bg-orange-500/10 border border-orange-500/20 text-sm text-orange-200 leading-relaxed">
                  Please wait <span className="text-white font-semibold">15 minutes</span> before requesting another reset link. This limit protects your account from abuse.
                </div>
                <Link href="/login">
                  <Button className="w-full h-12 text-lg font-bold bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 rounded-xl">
                    Back to sign in
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  className="w-full h-10 text-sm text-muted-foreground hover:text-white"
                  onClick={() => {
                    setPageState("form");
                    form.reset();
                  }}
                >
                  Try a different email
                </Button>
              </div>
            ) : (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <Label className="text-white/80">Email address</Label>
                        <FormControl>
                          <Input
                            placeholder="name@example.com"
                            className="bg-black/50 border-white/10 focus-visible:ring-primary focus-visible:border-primary text-white h-12 rounded-xl"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage className="text-destructive" />
                      </FormItem>
                    )}
                  />
                  <Button
                    type="submit"
                    className="w-full h-12 text-lg font-bold bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 rounded-xl mt-4"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send Reset Link"}
                  </Button>
                </form>
              </Form>
            )}
          </CardContent>

          <CardFooter className="flex justify-center border-t border-white/5 pt-6 mt-2">
            <p className="text-sm text-muted-foreground">
              Remembered it?{" "}
              <Link href="/login">
                <span className="text-primary hover:underline font-semibold cursor-pointer">Sign in</span>
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </PublicLayout>
  );
}
